/**
 * MCP Template Detail Page.
 *
 * Shows full template details including config schema, secrets schema,
 * tools, policies, and setup instructions. Provides a "Create from Template"
 * button that navigates to MCPServerCreatePage with pre-filled data.
 *
 * Sprint: 40, Task: S40-B04
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import {
  Button,
  Card,
  Spinner,
} from '@/components/ui';
import { useMCPCatalogTemplate } from '@/features/mcp';
import type { MCPTemplateTool } from '@/features/mcp';
import { SetupAssistantButton } from '@/features/mcp/components/setup-assistant-button';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Plus,
  Shield,
  Wrench,
  FileText,
  Settings,
  Key,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// Sub-components
// =============================================================================

function SectionHeader({ icon: Icon, title }: { icon: React.ComponentType<{ className?: string }>; title: string }) {
  return (
    <h3 className="flex items-center gap-2 font-semibold text-sm mb-3">
      <Icon className="w-4 h-4 text-primary" />
      {title}
    </h3>
  );
}

function ToolCard({ tool }: { tool: MCPTemplateTool }) {
  return (
    <div className="border rounded-lg p-3 space-y-1">
      <p className="font-mono text-xs font-medium">{tool.name}</p>
      <p className="text-xs text-muted-foreground">{tool.description}</p>
    </div>
  );
}

function PolicyRow({ label, value }: { label: string; value: string | number | boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{String(value)}</span>
    </div>
  );
}

function SecretField({ name, schema }: { name: string; schema: { title?: string; description?: string; type?: string; required?: boolean } }) {
  return (
    <div className="flex items-center justify-between py-2 text-xs border-b last:border-b-0">
      <div>
        <span className="font-medium">{schema.title ?? name}</span>
        {schema.description && (
          <p className="text-muted-foreground mt-0.5">{schema.description}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className={cn(
          'px-1.5 py-0.5 rounded text-[10px]',
          schema.type === 'password' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        )}>
          {schema.type === 'password' ? '●●●' : 'text'}
        </span>
        {schema.required && (
          <span className="text-destructive">*</span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Page Component
// =============================================================================

export function MCPTemplateDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const TOOLS_COLLAPSE_THRESHOLD = 6;
  const { data: template, isLoading, error } = useMCPCatalogTemplate(slug!);
  const [toolsExpanded, setToolsExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  if (error || !template) {
    return (
      <Card className="p-8 text-center">
        <p className="text-destructive">{t('admin.integrations.notFound')}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/integrations/catalog')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Catalog
        </Button>
      </Card>
    );
  }

  const policies = template.base_policies as Record<string, unknown>;
  const secretsSchema = template.secrets_schema as Record<string, Record<string, unknown>>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex items-center gap-4">
          <Link
            to="/integrations/catalog"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{template.display_name}</h1>
              {template.is_beta && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                  {t('admin.catalog.beta')}
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-1">{template.description}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button onClick={() => navigate(`/integrations/new?template=${template.slug}`)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.catalog.detail.createFromTemplate')}
          </Button>
          {template.setup_assistant && template.setup_assistant.mode !== 'manual' && (
            <SetupAssistantButton
              templateSlug={template.slug}
              mode={template.setup_assistant.mode}
              display={template.setup_assistant.display}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tools — collapsible when many */}
          <Card className="p-5">
            <button
              type="button"
              className="flex items-center justify-between w-full text-left"
              onClick={() => setToolsExpanded((prev) => !prev)}
            >
              <SectionHeader icon={Wrench} title={t('admin.catalog.detail.tools')} />
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-medium">
                  {template.default_tools.length} tools
                </span>
                {template.default_tools.length > TOOLS_COLLAPSE_THRESHOLD ? (
                  toolsExpanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )
                ) : null}
              </div>
            </button>
            {template.default_tools.length > 0 ? (
              template.default_tools.length <= TOOLS_COLLAPSE_THRESHOLD || toolsExpanded ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {template.default_tools.map((tool) => (
                    <ToolCard key={tool.name} tool={tool} />
                  ))}
                </div>
              ) : (
                <div className="mt-3 text-xs text-muted-foreground">
                  {template.default_tools.slice(0, 4).map((tool) => tool.name).join(', ')}
                  {template.default_tools.length > 4 && ` and ${template.default_tools.length - 4} more…`}
                </div>
              )
            ) : (
              <p className="text-xs text-muted-foreground mt-3">No tools defined</p>
            )}
          </Card>

          {/* Setup Instructions */}
          {template.setup_instructions && (
            <Card className="p-5">
              <SectionHeader icon={FileText} title={t('admin.catalog.detail.setupInstructions')} />
              <div className="whitespace-pre-wrap text-sm text-muted-foreground">
                {template.setup_instructions}
              </div>
            </Card>
          )}

          {/* Configuration Schema */}
          {template.config_schema && typeof template.config_schema === 'object' && (
            <Card className="p-5">
              <SectionHeader icon={Settings} title={t('admin.catalog.detail.configSchema')} />
              <pre className="bg-muted/50 rounded-lg p-3 text-xs font-mono overflow-x-auto max-h-64">
                {JSON.stringify(template.config_schema, null, 2)}
              </pre>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Required Credentials */}
          <Card className="p-5">
            <SectionHeader icon={Key} title={t('admin.catalog.detail.secretsSchema')} />
            {Object.keys(secretsSchema).length > 0 ? (
              <div>
                {Object.entries(secretsSchema).map(([name, schema]) => (
                  <SecretField key={name} name={name} schema={schema as Record<string, unknown>} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No credentials required</p>
            )}
          </Card>

          {/* Security Policies */}
          <Card className="p-5">
            <SectionHeader icon={Shield} title={t('admin.catalog.detail.policies')} />
            <div className="divide-y">
              {policies.max_calls_per_turn != null && (
                <PolicyRow label="Max calls / turn" value={policies.max_calls_per_turn as number} />
              )}
              {policies.max_timeout_seconds != null && (
                <PolicyRow label="Timeout" value={`${policies.max_timeout_seconds}s`} />
              )}
              {policies.rate_limit_per_minute != null && (
                <PolicyRow label="Rate limit / min" value={policies.rate_limit_per_minute as number} />
              )}
              {policies.rate_limit_per_hour != null && (
                <PolicyRow label="Rate limit / hour" value={policies.rate_limit_per_hour as number} />
              )}
              {policies.require_https != null && (
                <PolicyRow label="Require HTTPS" value={policies.require_https ? 'Yes' : 'No'} />
              )}
            </div>
          </Card>

          {/* Documentation Link */}
          {template.documentation_url && (
            <Card className="p-5">
              <SectionHeader icon={ExternalLink} title={t('admin.catalog.detail.documentation')} />
              <a
                href={template.documentation_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                {template.documentation_url}
                <ExternalLink className="w-3 h-3" />
              </a>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
